import React, { useState, useEffect, useReducer } from "react";
import openSocket from "../../services/socket-io";

import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  InputAdornment,
  TextField,
  Tabs,
  Tab,
} from "@material-ui/core";
import { Edit, DeleteOutline } from "@material-ui/icons";
import SearchIcon from "@material-ui/icons/Search";
import ImageIcon from "@material-ui/icons/Image";
import VideocamIcon from "@material-ui/icons/Videocam";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import TextFieldsIcon from "@material-ui/icons/TextFields";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import QuickAnswersModal from "../../components/QuickAnswersModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";

const reducer = (state, action) => {
  if (action.type === "LOAD_QUICK_ANSWERS") {
    const quickAnswers = action.payload;
    const newQuickAnswers = [];

    quickAnswers.forEach((quickAnswer) => {
      const quickAnswerIndex = state.findIndex((q) => q.id === quickAnswer.id);
      if (quickAnswerIndex !== -1) {
        state[quickAnswerIndex] = quickAnswer;
      } else {
        newQuickAnswers.push(quickAnswer);
      }
    });

    return [...state, ...newQuickAnswers];
  }

  if (action.type === "UPDATE_QUICK_ANSWERS") {
    const quickAnswer = action.payload;
    const quickAnswerIndex = state.findIndex((q) => q.id === quickAnswer.id);

    if (quickAnswerIndex !== -1) {
      state[quickAnswerIndex] = quickAnswer;
      return [...state];
    } else {
      return [quickAnswer, ...state];
    }
  }

  if (action.type === "DELETE_QUICK_ANSWERS") {
    const quickAnswerId = action.payload;

    const quickAnswerIndex = state.findIndex((q) => q.id === quickAnswerId);
    if (quickAnswerIndex !== -1) {
      state.splice(quickAnswerIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  typeTab: {
    minWidth: 64,
  },
  mediaThumb: {
    width: 36,
    height: 36,
    objectFit: "cover",
    borderRadius: 4,
  },
  typeIcon: {
    verticalAlign: "middle",
    marginRight: 4,
    fontSize: 18,
  },
}));

const TYPE_ALL = "";
const TYPE_TEXT = "text";
const TYPE_MEDIA = "media";
const TYPE_LOCATION = "location";

function getTypeLabel(type) {
  if (type === TYPE_TEXT) return i18n.t("quickAnswers.tabs.text");
  if (type === TYPE_MEDIA) return i18n.t("quickAnswers.tabs.media");
  if (type === TYPE_LOCATION) return i18n.t("quickAnswers.tabs.location");
  return type || "-";
}

function getTypeIcon(type, iconClass) {
  if (type === TYPE_TEXT) return <TextFieldsIcon className={iconClass} fontSize="small" />;
  if (type === TYPE_MEDIA) return <ImageIcon className={iconClass} fontSize="small" />;
  if (type === TYPE_LOCATION) return <LocationOnIcon className={iconClass} fontSize="small" />;
  return null;
}

const QuickAnswers = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [typeFilter, setTypeFilter] = useState(TYPE_ALL);
  const [quickAnswers, dispatch] = useReducer(reducer, []);
  const [selectedQuickAnswers, setSelectedQuickAnswers] = useState(null);
  const [quickAnswersModalOpen, setQuickAnswersModalOpen] = useState(false);
  const [deletingQuickAnswers, setDeletingQuickAnswers] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, typeFilter]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchQuickAnswers = async () => {
        try {
          const params = { searchParam, pageNumber };
          if (typeFilter) params.type = typeFilter;
          const { data } = await api.get("/quickAnswers/", { params });
          dispatch({ type: "LOAD_QUICK_ANSWERS", payload: data.quickAnswers });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchQuickAnswers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, typeFilter]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("quickAnswer", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUICK_ANSWERS", payload: data.quickAnswer });
      }

      if (data.action === "delete") {
        dispatch({
          type: "DELETE_QUICK_ANSWERS",
          payload: +data.quickAnswerId,
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenQuickAnswersModal = () => {
    setSelectedQuickAnswers(null);
    setQuickAnswersModalOpen(true);
  };

  const handleCloseQuickAnswersModal = () => {
    setSelectedQuickAnswers(null);
    setQuickAnswersModalOpen(false);
  };

  const handleSaveQuickAnswer = (data) => {
    dispatch({ type: "UPDATE_QUICK_ANSWERS", payload: data });
  };

  const handleEditQuickAnswers = (quickAnswer) => {
    setSelectedQuickAnswers(quickAnswer);
    setQuickAnswersModalOpen(true);
  };

  const handleDeleteQuickAnswers = async (quickAnswerId) => {
    try {
      await api.delete(`/quickAnswers/${quickAnswerId}`);
      toast.success(i18n.t("quickAnswers.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickAnswers(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingQuickAnswers &&
          `${i18n.t("quickAnswers.confirmationModal.deleteTitle")} ${
            deletingQuickAnswers.shortcut
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickAnswers(deletingQuickAnswers.id)}
      >
        {i18n.t("quickAnswers.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QuickAnswersModal
        open={quickAnswersModalOpen}
        onClose={handleCloseQuickAnswersModal}
        aria-labelledby="form-dialog-title"
        quickAnswerId={selectedQuickAnswers && selectedQuickAnswers.id}
        initialValues={selectedQuickAnswers}
        onSave={handleSaveQuickAnswer}
      />
      <MainHeader>
        <Title>{i18n.t("quickAnswers.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("quickAnswers.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenQuickAnswersModal}
          >
            {i18n.t("quickAnswers.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Tabs
        value={typeFilter === TYPE_ALL ? 0 : typeFilter === TYPE_TEXT ? 1 : typeFilter === TYPE_MEDIA ? 2 : 3}
        onChange={(_, v) => setTypeFilter([TYPE_ALL, TYPE_TEXT, TYPE_MEDIA, TYPE_LOCATION][v])}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab className={classes.typeTab} label={i18n.t("quickAnswers.tabs.all")} />
        <Tab className={classes.typeTab} label={i18n.t("quickAnswers.tabs.text")} />
        <Tab className={classes.typeTab} label={i18n.t("quickAnswers.tabs.media")} />
        <Tab className={classes.typeTab} label={i18n.t("quickAnswers.tabs.location")} />
      </Tabs>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("quickAnswers.table.type")}</TableCell>
              <TableCell align="center">{i18n.t("quickAnswers.table.title")}</TableCell>
              <TableCell align="center">{i18n.t("quickAnswers.table.message")}</TableCell>
              <TableCell align="center">{i18n.t("quickAnswers.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {quickAnswers.map((quickAnswer) => (
                <TableRow key={quickAnswer.id}>
                  <TableCell align="center">
                    {getTypeIcon(quickAnswer.type, classes.typeIcon)}
                    {getTypeLabel(quickAnswer.type)}
                  </TableCell>
                  <TableCell align="center">
                    {quickAnswer.title || quickAnswer.shortcut || "-"}
                  </TableCell>
                  <TableCell align="center">
                    {quickAnswer.type === TYPE_TEXT && quickAnswer.message}
                    {quickAnswer.type === TYPE_MEDIA && (
                      quickAnswer.mediaType === "image" && quickAnswer.mediaPath ? (
                        <img
                          src={`${getBackendUrl()}public/${quickAnswer.mediaPath}`}
                          alt=""
                          className={classes.mediaThumb}
                        />
                      ) : quickAnswer.mediaType === "video" ? (
                        <VideocamIcon fontSize="small" />
                      ) : (
                        <InsertDriveFileIcon fontSize="small" />
                      )
                    )}
                    {quickAnswer.type === TYPE_LOCATION && (quickAnswer.locationName || `${quickAnswer.latitude}, ${quickAnswer.longitude}`)}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditQuickAnswers(quickAnswer)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingQuickAnswers(quickAnswer);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default QuickAnswers;
